/**
 * Trip Sharing membership REST — mirrors iOS TripShareAPI.
 * Contract: backend/docs/trip-sharing-frontend-handoff.md
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

export type ShareRole = 'editor' | 'viewer';
export type MembershipStatus = 'pending' | 'active' | 'revoked';

export interface TripMembership {
  id: string;
  tripId: string;
  canonicalTripId: string;
  authorSub: string;
  memberSub: string;
  role: string;
  status: string;
  tripName?: string | null;
  invitedAt?: string | null;
  acceptedAt?: string | null;
  revokedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export class TripShareApiError extends Error {
  status: number;
  body?: string;

  constructor(status: number, message: string, body?: string) {
    super(message);
    this.name = 'TripShareApiError';
    this.status = status;
    this.body = body;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown, fallback = ''): string {
  if (value == null) return fallback;
  return String(value);
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.length > 0 ? s : null;
}

export function parseTripMembership(raw: unknown): TripMembership {
  const o = asRecord(raw);
  return {
    id: str(o.id),
    tripId: str(o.tripId),
    canonicalTripId: str(o.canonicalTripId),
    authorSub: str(o.authorSub),
    memberSub: str(o.memberSub),
    role: str(o.role),
    status: str(o.status),
    tripName: strOrNull(o.tripName),
    invitedAt: strOrNull(o.invitedAt),
    acceptedAt: strOrNull(o.acceptedAt),
    revokedAt: strOrNull(o.revokedAt),
    createdAt: strOrNull(o.createdAt),
    updatedAt: strOrNull(o.updatedAt),
  };
}

export function parseTripMembershipList(raw: unknown): TripMembership[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseTripMembership);
}

function encodePath(value: string): string {
  return encodeURIComponent(value).replace(/%2F/g, '%2F');
}

/** Match device-sync trip record suffixes (`{sub}#trip#{lowercase-uuid}`). */
function normalizedTripIdPath(tripId: string): string {
  return String(tripId ?? '').trim().toLowerCase();
}

async function mapError(res: Response): Promise<never> {
  const body = await res.text().catch(() => '');
  let message = `Trip share request failed (${res.status})`;
  try {
    const json = JSON.parse(body) as { detail?: string; title?: string };
    if (json.detail) message = json.detail;
    else if (json.title) message = json.title;
  } catch {
    /* keep default */
  }
  if (res.status === 409 && /cap|limit|20/i.test(message + body)) {
    throw new TripShareApiError(409, 'This trip already has the maximum number of members (20).', body);
  }
  if (res.status === 409) {
    throw new TripShareApiError(409, 'This invitation is no longer available', body);
  }
  throw new TripShareApiError(res.status, message, body);
}

export async function addMember(
  tripId: string,
  memberSub: string,
  role: ShareRole
): Promise<TripMembership> {
  const headers = await buildAuthHeaders(true);
  const res = await fetch(`${API_BASE_URL}/trips/${encodePath(normalizedTripIdPath(tripId))}/members`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ memberSub, role }),
  });
  if (!res.ok) await mapError(res);
  return parseTripMembership(await res.json());
}

export async function listMembers(tripId: string): Promise<TripMembership[]> {
  const headers = await buildAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/trips/${encodePath(normalizedTripIdPath(tripId))}/members`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) await mapError(res);
  return parseTripMembershipList(await res.json());
}

export async function patchMemberRole(
  tripId: string,
  memberSub: string,
  role: ShareRole
): Promise<TripMembership> {
  const headers = await buildAuthHeaders(true);
  const res = await fetch(
    `${API_BASE_URL}/trips/${encodePath(normalizedTripIdPath(tripId))}/members/${encodePath(memberSub)}`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ role }),
    }
  );
  if (!res.ok) await mapError(res);
  return parseTripMembership(await res.json());
}

export async function revokeMember(tripId: string, memberSub: string): Promise<TripMembership> {
  const headers = await buildAuthHeaders();
  const res = await fetch(
    `${API_BASE_URL}/trips/${encodePath(normalizedTripIdPath(tripId))}/members/${encodePath(memberSub)}`,
    { method: 'DELETE', headers }
  );
  if (!res.ok) await mapError(res);
  return parseTripMembership(await res.json());
}

export async function listTripSharesPage(
  cursor?: string | null,
  limit = 100
): Promise<{ items: TripMembership[]; nextCursor: string | null }> {
  const headers = await buildAuthHeaders();
  const params = new URLSearchParams({ limit: String(Math.min(Math.max(limit, 1), 100)) });
  if (cursor) params.set('cursor', cursor);
  const res = await fetch(`${API_BASE_URL}/trip-shares?${params}`, { method: 'GET', headers });
  if (!res.ok) await mapError(res);
  const items = parseTripMembershipList(await res.json());
  const next = res.headers.get('Next-Cursor')?.trim() || null;
  return { items, nextCursor: next || null };
}

export async function listAllTripShares(): Promise<TripMembership[]> {
  const all: TripMembership[] = [];
  let cursor: string | null = null;
  do {
    const page = await listTripSharesPage(cursor);
    all.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor);
  return all;
}

export async function acceptTripShare(membershipId: string): Promise<TripMembership> {
  const headers = await buildAuthHeaders(true);
  const res = await fetch(`${API_BASE_URL}/trip-shares/${encodePath(membershipId)}/accept`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) await mapError(res);
  return parseTripMembership(await res.json());
}

export async function declineOrLeaveTripShare(membershipId: string): Promise<void> {
  const headers = await buildAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/trip-shares/${encodePath(membershipId)}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) await mapError(res);
}

/** Active memberships → local trip ids for visibility. */
export function activeSharedTripIdsFromMemberships(memberships: TripMembership[]): Set<string> {
  const ids = new Set<string>();
  const addId = (raw: string | null | undefined): void => {
    const s = String(raw ?? '').trim();
    if (!s) return;
    ids.add(s);
    ids.add(s.toLowerCase());
    // Bare UUID from `author#trip#{uuid}` (API may put canonical form in tripId).
    const parts = s.split('#');
    if (parts.length === 3 && parts[1] === 'trip' && parts[2]) {
      ids.add(parts[2]);
      ids.add(parts[2].toLowerCase());
    }
  };
  for (const m of memberships) {
    if (String(m.status ?? '').trim().toLowerCase() !== 'active') continue;
    addId(m.tripId);
    addId(m.canonicalTripId);
  }
  return ids;
}

export function pendingMemberships(memberships: TripMembership[]): TripMembership[] {
  return memberships.filter(
    (m) => String(m.status ?? '').trim().toLowerCase() === 'pending'
  );
}

export function activeMemberships(memberships: TripMembership[]): TripMembership[] {
  return memberships.filter(
    (m) => String(m.status ?? '').trim().toLowerCase() === 'active'
  );
}
